import i18next from 'i18next';
import { curry } from 'ramda';
import { isCnp } from 'utils/simple-validations';
import { PDFDocument } from 'pdf-lib';
import { format } from 'date-fns';
import { emptyString } from 'constants/emptyConst';

export const objToQueryString = (obj, overrides) => {
  const copy = Object.assign(obj, overrides || {});
  const searchParams = new URLSearchParams();
  Object.entries(copy).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length) {
      value.forEach(v => { if (typeof v !== 'object') searchParams.append(key, v); });
    } else {
      if (value !== '' && value !== null && value !== undefined && typeof value !== 'object') searchParams.set(key, value);
    }
  });
  return searchParams.toString();
};

export const clean = obj => {
  Object.keys(obj).forEach(
    key =>
      (obj[key] == null || obj[key] === undefined || obj[key] === '' || (typeof obj[key] === 'number' && isNaN(obj[key]))) &&
      delete obj[key]
  );
};

export const getRandomInt = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min)) + min;
};

export const getRandomNegativeInt = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return (Math.floor(Math.random() * (max - min)) + min) * -1;
};

export const getInt = nr => {
  let o = parseInt(nr, 10);
  if (isNaN(o)) return null;
  return o;
};

export const valueOrDefault = curry(($default, value) => value ?? $default);

export const changeValueInArray = (list, keyName, keyValue, propertyName, propertyValue) => {
  return list.map(el => (el[keyName] === keyValue ? Object.assign({}, el, { [propertyName]: propertyValue }) : el));
};

export const changeValuesInArray = (list, keyName, keyValue, properties) => {
  return list.map(el => (el[keyName] === keyValue ? Object.assign({}, el, { ...properties }) : el));
};

export const isEmpty = obj => {
  for (var key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) return false;
  }
  return true;
};

export const groupBy = (array, f) => {
  let groups = {};
  array.forEach(function (o) {
    var group = JSON.stringify(f(o));
    groups[group] = groups[group] || [];
    groups[group].push(o);
  });
  return Object.keys(groups).map(function (group) {
    return groups[group];
  });
};

export const extractAgeFromCnp = p_cnp => {
  if (p_cnp && isCnp(p_cnp)) {
    // Extract birthday
    const extraDateValues = '00:00:00 +0000';
    const sex = parseInt(p_cnp[0], 10);
    let year = parseInt(`${p_cnp[1]}${p_cnp[2]}`, 10);
    const month = parseInt(`${p_cnp[3]}${p_cnp[4]}`, 10);
    const day = parseInt(`${p_cnp[5]}${p_cnp[6]}`, 10);

    switch (sex) {
      case 1:
      case 2:
        year += 1900;
        break;
      case 3:
      case 4:
        year += 1800;
        break;
      case 5:
      case 6:
        year += 2000;
        break;
      case 7:
      case 8:
      case 9:
        year += 2000;
        if (year > new Date().getFullYear() - 18) {
          year -= 100;
        }
        break;
      default: {
        return undefined;
      }
    }

    if (year < 1800 || year > 2099) {
      return undefined;
    }

    const birthday = Date.parse(`${year}-${month}-${day} ${extraDateValues}`);

    // Extract age
    const diff_ms = Date.now() - birthday;
    const age_dt = new Date(diff_ms);
    return age_dt.getUTCFullYear() - 1970;
  }

  return undefined;
};

export const CombineKeys = (key1, key2) => {
  return `${key1}_${key2}`;
};

export const areEqual = propsExcluded => (prevProps, nextProps) => {
  return Object.keys(prevProps)
    .filter(x => !propsExcluded.includes(x))
    .every(key => {
      return prevProps[key] === nextProps[key];
    });
};

export const findFirstCandidate = (candidates, pool, equalFunc = (candidate, poolItem) => candidate === poolItem) =>
  candidates.find(c => pool.some(p => equalFunc(c, p)));

export const combineStyles = (...styles) => {
  return function CombineStyles(theme) {
    const outStyles = styles.map(arg => {
      // Apply the "theme" object for style functions.
      if (typeof arg === 'function') {
        return arg(theme);
      }
      // Objects need no change.
      return arg;
    });

    return outStyles.reduce((acc, val) => Object.assign(acc, val));
  };
};

export const getOidcConfigName = () => {
  const tid = 'config_show_access_token';
  return tid;
};

export function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

const sortAscending = (list, property) => {
  return list.slice().sort((a, b) => (a[property] === b[property] ? 0 : a[property] > b[property] ? 1 : -1));
};

const sortDescending = (list, property) => {
  return list.slice().sort((a, b) => (a[property] === b[property] ? 0 : a[property] < b[property] ? 1 : -1));
};

export const sortList = (list, property, descending) => (descending ? sortDescending(list, property) : sortAscending(list, property));

export function convertBase64ToByteArray(base64File) {
  const byteCharacters = atob(base64File);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Uint8Array(byteNumbers);
}

export const convertBase64ToBlob = (base64, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const convertBase64ToObjectUrl = (base64, contentType = '', sliceSize = 512) => {
  const blob = convertBase64ToBlob(base64, contentType, sliceSize);
  return URL.createObjectURL(blob);
};

export const getDateFormat = () => {
  switch (i18next.language) {
    case 'ro-RO':
      return 'ro';
    case 'en-GB':
      return 'en-US';
    case 'fr-FR':
      return 'fr';
    default:
      return 'ro';
  }
};

export const getDateValue = value => {
  return Date.parse(value) && new Date(value) > new Date('01/01/1900') ? format(new Date(value), 'yyyy-MM-dd').toString() : emptyString;
};

export const copyToClipboard = text => () => navigator.clipboard.writeText(text);

export const getDynamicFieldControlType = typeCode => {
  switch (typeCode) {
    case 'TP_TEXT':
      return 'Text';
    case 'COMBO':
    case 'SEARCH':
    case 'SELECT':
    case 'SEARCH_BIT':
    case 'ADAPTIVE_LOOKUP':
      return 'Autocomplete';
    case 'TP_NUM':
    case 'AMOUNT':
    case 'INT':
      return 'Numeric';
    case 'TP_DATE':
    case 'DATE':
      return 'Date';
    case 'BOOL':
      return 'Checkbox';
    case 'PHONE_NUMBER':
      return 'Custom';
    default:
      return 'Text';
  }
};

export const getDynamicFieldProperties = typeCode => {
  switch (typeCode) {
    case 'TP_TEXT':
      return { controlType: 'Text' };
    case 'COMBO':
    case 'COMBO_LAZY':
    case 'SEARCH':
    case 'SELECT':
    case 'SEARCH_BIT':
    case 'AUTOCOMPLETE':
    case 'ADAPTIVE_LOOKUP':
      return { controlType: 'Autocomplete' };
    case 'ADAPTIVE_LOOKUP_MULTI':
      return { controlType: 'Autocomplete', isMultiSelection: true };
    case 'TP_NUM':
    case 'AMOUNT':
      return { controlType: 'Numeric' };
    case 'TP_DATE':
    case 'YEAR':
    case 'MONTHYEAR':
    case 'DATE':
      return { controlType: 'Date' };
    case 'BOOL':
      return { controlType: 'Checkbox' };
    case 'CHECK_BOX_INDETERMINATE':
      return { controlType: 'Checkbox', indeterminate: true };
    default:
      return { controlType: typeCode || 'Text' };
  }
};

export function hexToHSL(H) {
  // Convert hex to RGB first
  let r = 0,
    g = 0,
    b = 0;
  if (H.length == 4) {
    r = '0x' + H[1] + H[1];
    g = '0x' + H[2] + H[2];
    b = '0x' + H[3] + H[3];
  } else if (H.length == 7) {
    r = '0x' + H[1] + H[2];
    g = '0x' + H[3] + H[4];
    b = '0x' + H[5] + H[6];
  }
  // Then to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  let cmin = Math.min(r, g, b),
    cmax = Math.max(r, g, b),
    delta = cmax - cmin,
    h = 0,
    s = 0,
    l = 0;

  if (delta == 0) h = 0;
  else if (cmax == r) h = ((g - b) / delta) % 6;
  else if (cmax == g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  h = Math.round(h * 60);

  if (h < 0) h += 360;

  l = (cmax + cmin) / 2;
  s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return 'hsl(' + h + ',' + s + '%,' + l + '%)';
}

export const convertImageToPdfBytes = async imageFile => {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([600, 774]);

  const fileExtension = imageFile.name.split('.').pop().toLowerCase();
  const imageBytes = new Uint8Array(await imageFile.arrayBuffer());

  let image;
  switch (fileExtension) {
    case 'jpg':
    case 'jpeg':
      if (isJpg(imageBytes)) image = await pdfDoc.embedJpg(imageBytes);
      else throw new Error('BulkPdf.ImageMalformed');
      break;
    case 'png':
      if (isPng(imageBytes)) image = await pdfDoc.embedPng(imageBytes);
      else throw new Error('BulkPdf.ImageMalformed');
      break;
    default:
      throw new Error('BulkPdf.UnsupportedImageFormat');
  }

  page.drawImage(image, {
    x: 50,
    y: 50,
    width: 500,
    height: 674,
    opacity: 1
  });

  return await pdfDoc.save();
};

export const uploadedFileIsImage = fileName => {
  const imageExtensions = ['.jpg', '.jpeg', '.png'];
  return imageExtensions.some(x => fileName.toLowerCase().endsWith(x));
};

export const getListFilterLocalizedStrings = t => ({
  ResetFilters: t('Tooltips.ResetFilters'),
  ShowFilters: t('Tooltips.ShowFilters'),
  ChooseFields: t('Tooltips.ChooseFields'),
  ChooseFilters: t('Tooltips.ChooseFilters'),
  OrderBy: t('Tooltips.OrderBy'),
  OrderAscending: t('Tooltips.OrderAscending'),
  OrderDescending: t('Tooltips.OrderDescending'),
  InOrder: t('Tooltips.InOrder'),
  UserPreference: t('ListFilter.UserPreference'),
  UserPreferences: t('ListFilter.UserPreferences'),
  EditUserPreferences: t('ListFilter.EditUserPreferences'),
  DeleteUserPreference: t('ListFilter.DeleteUserPreference'),
  Download: t('Tooltips.Download'),
  Add: t('Add'),
  Close: t('Close'),
  Delete: t('Delete'),
  FilterName: t('ListFilter.FilterName'),
  Implicit: t('ListFilter.Implicit'),
  Search: t('Search')
});

export const isValidNumber = value => !isNaN(value) && value !== null;

const isJpg = bytes => {
  // Check JPG magic number
  return bytes[0] === 0xff && bytes[1] === 0xd8;
};

const isPng = bytes => {
  // Check PNG magic number
  return [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((byte, index) => bytes[index] === byte);
};
